import "./canvas-toolbar.scss";
import { memo } from "react";
import { Editor } from "@tiptap/react";

import { Icon, Toolbar, SidebarMenu } from "utils/ui";

import { useTextmenuCommands } from "../menus/TextMenu/hooks/useTextmenuCommands";
import { useTextmenuStates } from "../menus/TextMenu/hooks/useTextmenuStates";
import { useTextmenuContentTypes } from "../menus/TextMenu/hooks/useTextmenuContentTypes";
import { ContentTypeDropdown } from "../menus/TextMenu/components/ContentTypeDropdown";
import { EditLinkPopover } from "../menus/TextMenu/components/EditLinkPopover";

const MemoButton = memo(Toolbar.Button);
const MemoContentTypePicker = memo(ContentTypeDropdown);

/**
 * Persistent formatting bar pinned to the top of the canvas. Replaces the old
 * floating bubble menu — fixed surfaces are clearer for less tech-savvy users.
 * Reuses the same command/state hooks the bubble used, so behaviour is identical.
 */
export const CanvasToolbar = ({ editor }: { editor: Editor }) => {
  const commands = useTextmenuCommands(editor);
  const states = useTextmenuStates(editor);
  const blockOptions = useTextmenuContentTypes(editor);

  return (
    <div
      className="canvas-toolbar w-full px-3 flex items-center justify-around overflow-x-auto overflow-y-hidden"
      style={{
        flex: "none",
        borderBottom: "1px solid var(--surface-border)",
        background: "var(--surface-b)",
      }}
    >
      <Toolbar.Wrapper>
        <MemoButton tooltip="Undo" tooltipShortcut={["Mod", "Z"]} onClick={() => editor.commands.undo()}>
          <Icon name="Undo" />
        </MemoButton>
        <MemoButton tooltip="Redo" tooltipShortcut={["Mod", "Shift", "Z"]} onClick={() => editor.commands.redo()}>
          <Icon name="Redo" />
        </MemoButton>

        <Toolbar.Divider />

        <div style={{ width: "180px" }}>
          <MemoContentTypePicker options={blockOptions} />
        </div>

        <Toolbar.Divider />

        <MemoButton tooltip="Bold" tooltipShortcut={["Mod", "B"]} onClick={commands.onBold} active={states.isBold}>
          <Icon name="Bold" />
        </MemoButton>
        <MemoButton tooltip="Italic" tooltipShortcut={["Mod", "I"]} onClick={commands.onItalic} active={states.isItalic}>
          <Icon name="Italic" />
        </MemoButton>
        <MemoButton
          tooltip="Underline"
          tooltipShortcut={["Mod", "U"]}
          onClick={commands.onUnderline}
          active={states.isUnderline}
        >
          <Icon name="Underline" />
        </MemoButton>

        <Toolbar.Divider />

        <MemoButton
          tooltip="Align left"
          tooltipShortcut={["Shift", "Mod", "L"]}
          onClick={commands.onAlignLeft}
          active={states.isAlignLeft}
        >
          <Icon name="AlignLeft" />
        </MemoButton>
        <MemoButton
          tooltip="Align center"
          tooltipShortcut={["Shift", "Mod", "E"]}
          onClick={commands.onAlignCenter}
          active={states.isAlignCenter}
        >
          <Icon name="AlignCenter" />
        </MemoButton>
        <MemoButton
          tooltip="Align right"
          tooltipShortcut={["Shift", "Mod", "R"]}
          onClick={commands.onAlignRight}
          active={states.isAlignRight}
        >
          <Icon name="AlignRight" />
        </MemoButton>

        <Toolbar.Divider />

        <MemoButton
          tooltip="Bullet List"
          onClick={commands.toggleBulletList}
          active={states.isBulletList}
          children={<Icon name="List" />}
        />
        <MemoButton
          tooltip="Numbered List"
          onClick={commands.toggleOrderedList}
          active={states.isOrderedList}
          children={<Icon name="ListOrdered" />}
        />

        <Toolbar.Divider />

        <EditLinkPopover onSetLink={commands.onLink} />

        <MemoButton tooltip="Clear formatting" onClick={commands.onClearFormatting}>
          <Icon name="RemoveFormatting" />
        </MemoButton>

        <Toolbar.Divider />

        <SidebarMenu.ColorPicker
          currentColor={states.currentColor}
          onChangeColor={commands.onChangeColor}
          onClearColor={commands.onClearColor}
          className="h-8 w-8 min-w-8 p-0 flex items-center justify-center hover:opacity-90"
        />
      </Toolbar.Wrapper>
    </div>
  );
};
